import { useCallback, useEffect } from 'react';

import { useNavigate } from 'react-router';

import { useMarketsList } from '@/features/Markets';
import { useUserConfig } from '@/features/user-config';
import { FeatureFlag } from '@/services/feature-flags';
import { useFlag } from '@/services/feature-flags/react';

import {
  getFallbackMarketId,
  navigateToTarget,
  resolveHomepageTarget,
  shouldDelayNavigation
} from './HomepageRedirect.utils';

export const HomepageRedirect = () => {
  const navigate = useNavigate();

  const { data: marketsListData, isSuccess: isMarketsListSuccess } = useMarketsList();
  const marketsList = marketsListData?.marketsList;

  const { data: userConfigData, isSuccess: isUserConfigSuccess } = useUserConfig();

  const defaultMarketId = userConfigData?.defaultMarket?.value;
  const homepage = userConfigData?.homepage?.value;

  const isPersonalizationEnabled = useFlag(FeatureFlag.PersonalizationEnabled);

  const isMarketValid = useCallback(
    (marketId: string) => (marketsList ?? []).some(marketItem => marketItem.marketId === marketId),
    [marketsList]
  );

  useEffect(() => {
    if (
      shouldDelayNavigation(
        isMarketsListSuccess,
        isUserConfigSuccess,
        marketsList?.length ?? 0,
        isPersonalizationEnabled
      )
    ) {
      return;
    }

    const homepageTarget = resolveHomepageTarget(
      homepage,
      Boolean(isPersonalizationEnabled),
      isMarketValid
    );

    if (homepageTarget) {
      navigateToTarget(navigate, homepageTarget);
      return;
    }

    const targetMarketId = getFallbackMarketId(defaultMarketId, marketsList ?? [], isMarketValid);
    if (targetMarketId) {
      navigate(`/markets/${targetMarketId}`, { replace: true });
    }
  }, [
    isMarketsListSuccess,
    isUserConfigSuccess,
    marketsList,
    defaultMarketId,
    homepage,
    isPersonalizationEnabled,
    isMarketValid,
    navigate
  ]);

  return null;
};
